import { BookingFormGuestsStorage } from './lib/booking_form_guests_storage';
import { ClientFormStorage } from './lib/client_form_storage';

/**
 * @typedef {Partial<Record<keyof ClientFormData, JQuery>>} ClientFormInputs словарь полей формы, привязанных к полям ClientFormData
 */

/**
 * @typedef {Partial<Record<keyof BookingFormGuestData, JQuery>>} BookingFormGuestInputs словарь полей формы, привязанных к полям BookingFormGuestData
 */

/**
 * Синхронизация полей формы бронирования/запроса с данными клиента из localStorage:
 * - поля заполняются данными из localStorage,
 * - изменения полей записываются в localStorage.
 * @param {'booking'|'party'} formType
 */
export function syncClientFormData(formType) {
  /** @type ClientFormInputs */
  const clientFormInputs = {};
  /** @type {Array<BookingFormGuestInputs>} */
  const bookingFormGuestInputs = [];

  // Найти все поля формы, которые можно запонить данными из localStorage
  if (formType === 'booking') {
    initBookingFormInputs(clientFormInputs);

    // Находим поля формы с информацией о гостях (только для формы бронирования)
    initGuestsInputs(bookingFormGuestInputs);

    // Синхронизировать кол-во гостей в localStorage и форме бронирования
    BookingFormGuestsStorage.setSize(bookingFormGuestInputs.length);
  } else if (formType === 'party') {
    initPartyFormInputs(clientFormInputs);
  } else {
    return;
  }

  for (const field in clientFormInputs) {
    setClientFormInputValue(field, clientFormInputs[field]);

    // Сохранять изменения поля в localStorage
    bindFormInputHandlers(clientFormInputs[field], field, ClientFormStorage.set);
  }

  for (let i = 0; i < bookingFormGuestInputs.length; i++) {
    const guestInputs = bookingFormGuestInputs[i];

    for (const field in guestInputs) {
      const value = BookingFormGuestsStorage.get(i, field);
      if (value) {
        guestInputs[field].val(value).attr('value', value).trigger('input').trigger('change');
      }

      bindFormInputHandlers(guestInputs[field], field, (_, value) => BookingFormGuestsStorage.set(i, field, value));
    }
  }
}

/**
 * Найти все поля формы бронирования
 * @param {ClientFormInputs} clientFormInputs
 */
function initBookingFormInputs(clientFormInputs) {
  initFormInput(clientFormInputs, 'name', 'input#booking-user-name');
  initFormInput(clientFormInputs, 'lastName', 'input#booking-user-lastname');
  initFormInput(clientFormInputs, 'patronymic', 'input#booking-user-patronymic');
  initFormInput(clientFormInputs, 'phoneNumber', 'input#booking-user-phone');
  initFormInput(clientFormInputs, 'email', 'input#booking-user-email');
  initFormInput(clientFormInputs, 'birthday', 'input#booking-guest-birthday');
  initFormInput(clientFormInputs, 'comment', '#comment');
  initFormInput(clientFormInputs, 'withPet', '#with_pet');
  initFormInput(clientFormInputs, 'petInfo', '#booking-pet-info');
  initFormInput(
    clientFormInputs,
    'realArrival',
    window.isMobileDevice ? 'input#real_arrival_input' : 'input#real_arrival'
  );
}

/**
 * Найти все поля формы запроса
 * @param {ClientFormInputs} clientFormInputs
 */
function initPartyFormInputs(clientFormInputs) {
  initFormInput(clientFormInputs, 'name', 'input#client_name');
  initFormInput(clientFormInputs, 'lastName', 'input#client_last_name');
  initFormInput(clientFormInputs, 'phoneNumber', 'input#client_phone');
  initFormInput(clientFormInputs, 'email', 'input#client_email');
  initFormInput(clientFormInputs, 'comment', '#comment');
}

/**
 * Найти все поля для каждого гостя из формы бронирования
 * @param {Array<BookingFormGuestInputs>} guestsInputs
 */
function initGuestsInputs(guestsInputs) {
  // На ходу определяем количество гостей по количеству полей ввода имени для гостей
  let index = 0;
  while (true) {
    const guestInputsRecord = {};
    const nameFieldExists = initFormInput(guestInputsRecord, 'name', `#allGuests input#booking-guest-name_${index}`);
    if (!nameFieldExists) return;

    initFormInput(guestInputsRecord, 'lastname', `#allGuests input#booking-guest-lastname_${index}`);
    initFormInput(guestInputsRecord, 'birthday', `#allGuests input#booking-guest-birthday_${index}`);
    initFormInput(guestInputsRecord, 'patronymic', `#allGuests input#booking-guest-patronymic_${index}`);
    guestsInputs.push(guestInputsRecord);
    index++;
  }
}

/**
 * Поиск элемента input/textarea для заданного поля формы
 * @param {Record<string, JQuery>} formInputsRecord
 * @param {string} field
 * @param {string} query
 * @return {boolean}
 */
function initFormInput(formInputsRecord, field, query) {
  const $input = $(query);
  if ($input.length) {
    formInputsRecord[field] = $input;
  }
  return !!$input.length;
}

/**
 * Настроить сохранение изменений input-элемента в localStorage
 * @param $input
 * @param {keyof ClientFormData} field
 * @param {function} saveCallback функция сохранения
 */
function bindFormInputHandlers($input, field, saveCallback) {
  if (!$input.length) return;

  const isCheckbox = $input[0].type === 'checkbox';
  let timer = null;

  if (!isCheckbox) {
    $input.on('input', ({ target: { value } }) => {
      clearTimeout(timer);
      timer = setTimeout(() => saveCallback(field, value), 1000);
    });
  }
  $input.on('change', ({ target: { value } }) => {
    clearTimeout(timer);
    if (isCheckbox) {
      saveCallback(field, $input.is(':checked'));
    } else {
      saveCallback(field, value);
    }
  });
}

/**
 * Заполинть поле данными пользователя из localStorage
 * @param {keyof ClientFormData} field
 * @param {JQuery} $input
 */
function setClientFormInputValue(field, $input) {
  if ($input[0].type === 'checkbox') {
    setClientFormCheckboxValue(field, $input);
    return;
  }

  const value = ClientFormStorage.get(field);
  if (value && shouldWriteCacheValue($input, field)) {
    $input.val(value).attr('value', value);
    if (field === 'realArrival' && value !== 'Я не знаю') {
      if (window.isMobileDevice) {
        $('#real_arrival_select .ui-select__value').text(value);
        $('#real_arrival_input').val(value);
      } else {
        const $mtuiRealArrival = $('#mtui_real_arrival');
        $mtuiRealArrival.find(`.mtui-select__option[data-value="${value}"]`).trigger('click');
        $mtuiRealArrival.trigger('click'); // Триггерим повторно клик, чтобы закрыть список
      }
    } else {
      $input.trigger('input').trigger('change');
    }
  } else if ($input.val()) {
    ClientFormStorage.set(field, $input.val());
  }
}

/**
 * Восстановить/сохранить состояние чекбокса с учётом приоритета URL.
 * @param {keyof ClientFormData} field
 * @param {JQuery} $input
 */
function setClientFormCheckboxValue(field, $input) {
  const checked = $input.is(':checked');

  if (new URLSearchParams(window.location.search).has('with_pet')) {
    ClientFormStorage.set(field, checked);
    return;
  }

  const cached = ClientFormStorage.get(field);
  if (cached === undefined) {
    ClientFormStorage.set(field, checked);
    return;
  }

  const shouldBeChecked = cached === true || cached === 'true';
  if (checked !== shouldBeChecked) {
    $input.prop('checked', shouldBeChecked).trigger('change');
  }
}

/**
 * Проверить, нужно ли вписывать значение из кэша
 * @param {JQuery} $input
 * @param {keyof BookingFormGuestData | keyof ClientFormData} field
 * @return boolean
 */
function shouldWriteCacheValue($input, field) {
  if (!$input.val()) {
    return true;
  } else if (field === 'phoneNumber' && !/^\d+$/.test($input.val())) {
    return true;
  } else if (field === 'realArrival') {
    return true;
  } else {
    return false;
  }
}
